import {
  Input,
  Component,
  EventEmitter,
  Output,
  OnInit,
  Pipe,
  PipeTransform,
} from "@angular/core";
import { ColorSelectorComponent } from "./color-selector.component";
import { GoogleFontPickerComponent } from "./google-font-picker.component";
import { BorderSelectorComponent } from "./border-selector.component";
import { CommonModule } from "@angular/common";
import { firstValueFrom } from "rxjs";
import { DropdownModule } from "primeng/dropdown";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { ColorPickerModule } from "ngx-color-picker";
import { ButtonModule } from "primeng/button";
import { parse } from "css";
import { AccordionModule } from "primeng/accordion";
import { FieldsetModule } from "primeng/fieldset";
import { BorderRadiusComponent } from "./border-radius.component";
import { TabViewModule } from "primeng/tabview";
import { TreeTableModule } from "primeng/treetable";
import { AssetsPickerComponent } from "./assets-picker.component";
import * as _ from "lodash";
import { HttpClient } from "@angular/common/http";
import { environment } from "environments/environment";
import { SimplifyColorsPipe } from "@app/pipe/simplify-colors.pipe";

@Component({
  imports: [
    CommonModule,
    DropdownModule,
    AccordionModule,
    FormsModule,
    InputTextModule,
    ColorPickerModule,
    FieldsetModule,
    TabViewModule,
    TreeTableModule,
    ColorSelectorComponent,
    GoogleFontPickerComponent,
    BorderSelectorComponent,
    BorderRadiusComponent,
    ButtonModule,
    AssetsPickerComponent,
    SimplifyColorsPipe,
  ],
  standalone: true,
  selector: "nis-theme-editor",
  templateUrl: "./theme-editor.component.html",
  styles: [
    `
      :host ::ng-deep .p-fieldset {
        border: none;
      }
      :host ::ng-deep .p-fieldset .p-fieldset-legend {
        padding: 0 0 8px 0;
        border: none;
        border-bottom: 1px solid gray;
        text-align: left;
      }
      :host ::ng-deep .p-fieldset .p-fieldset-legend a {
        float: left;
        width: 100%;
      }
    `,
  ],
})
export class ThemeEditorComponent implements OnInit {
  cols: any[] = [
    { field: "name", header: "Name", class: "w-32" },
    { field: "value", header: "Value", class: "w-48" },
    { field: "trash", header: "Trash", class: "w-24" },
  ];
  currentPageType: string = "";
  currentSections = [];
  _branding: string = "";
  _style: string = "";
  activeSectionIndex: number = 0;
  rootCSS = `html {
  font-size: 8px;
  }`;
  verticalAlignOptions = [
    {
      code: "baseline",
    },
    {
      code: "top",
    },
    {
      code: "middle",
    },
    {
      code: "bottom",
    },
    {
      code: "sub",
    },
    {
      code: "text-top",
    },
  ];
  textTransformOptions = [
    {
      code: "none",
    },
    {
      code: "uppercase",
    },
    {
      code: "lowercase",
    },
    {
      code: "capitalize",
    },
  ];
  textAlignOptions = [
    {
      code: "start",
    },
    {
      code: "end",
    },
    {
      code: "center",
    },
    {
      code: "justify",
    },
  ];
  backgroundRepeatOptions = [
    {
      code: "repeat",
    },
    {
      code: "no-repeat",
    },
  ];
  backgroundSizeOptions = [
    {
      code: "contain",
    },
    {
      code: "cover",
    },
  ];
  backgroundPositionOptions = [
    {
      code: "top",
    },
    {
      code: "bottom",
    },
    {
      code: "left",
    },
    {
      code: "right",
    },
    {
      code: "center",
    },
  ];
  floatOptions = [
    {
      code: "left",
    },
    {
      code: "right",
    },
  ];
  displayOptions = [
    {
      code: "none",
    },
    {
      code: "-webkit-box",
    },
    {
      code: "block",
    },
    {
      code: "inline",
    },
    {
      code: "inline-block",
    },
    {
      code: "flex",
    },
    {
      code: "inline-flex",
    },
    {
      code: "grid",
    },
    {
      code: "inline-grid",
    },
  ];
  listStyleOptions = [
    {
      code: "none",
    },
    {
      code: "disc",
    },
    {
      code: "circle",
    },
    {
      code: "square",
    },
  ];
  letterSpacingOptions = [
    {
      code: "-0.05em",
    },
    {
      code: "-0.025em",
    },
    {
      code: "0em",
    },
    {
      code: "0.025em",
    },
    {
      code: "0.05em",
    },
    {
      code: "0.1em",
    },
  ];

  lineHeightOptions = [
    {
      code: ".75rem",
    },
    {
      code: "1rem",
    },
    {
      code: "1.25rem",
    },
    {
      code: "1.5rem",
    },
    {
      code: "1.75rem",
    },
    {
      code: "2rem",
    },
    {
      code: "2.25rem",
    },
    {
      code: "2.5rem",
    },
    {
      code: "1",
    },
    {
      code: "1.25",
    },
    {
      code: "1.375",
    },
    {
      code: "1.5",
    },
    {
      code: "1.625",
    },
    {
      code: "2",
    },
  ];

  directionOptions = [{ code: "normal" }, { code: "reverse" }];

  orientationOptions = [{ code: "horizontal" }, { code: "vertical" }];

  fontWeightOptions = [
    {
      code: "100",
    },
    {
      code: "200",
    },
    {
      code: "300",
    },
    {
      code: "400",
    },
    {
      code: "500",
    },
    {
      code: "600",
    },
    {
      code: "700",
    },
    {
      code: "800",
    },
    {
      code: "900",
    },
  ];

  fontSizeOptions = [
    {
      code: "0.75rem",
    },
    {
      code: "0.875rem",
    },
    {
      code: "1rem",
    },
    {
      code: "1.125rem",
    },
    {
      code: "1.5rem",
    },
    {
      code: "1.875rem",
    },
    {
      code: "2.25rem",
    },
    {
      code: "3rem",
    },
    {
      code: "3.75rem",
    },
    {
      code: "4.5rem",
    },
    {
      code: "6rem",
    },
    {
      code: "8rem",
    },
  ];

  dimensionOptions = [
    {
      code: "auto",
    },
    {
      code: "0px",
    },
    {
      code: "1px",
    },
    {
      code: "0.125rem",
    },
    {
      code: "0.25rem",
    },
    {
      code: "0.375rem",
    },
    {
      code: "0.5rem",
    },
    {
      code: "0.625rem",
    },
    {
      code: "0.75rem",
    },
    {
      code: "0.875rem",
    },
    {
      code: "1rem",
    },
    {
      code: "1.25rem",
    },
    {
      code: "1.5rem",
    },
    {
      code: "1.75rem",
    },
    {
      code: "2rem",
    },
    {
      code: "2.25rem",
    },
    {
      code: "2.5rem",
    },
    {
      code: "2.75rem",
    },
    {
      code: "3rem",
    },
    {
      code: "3.5rem",
    },
    {
      code: "4rem",
    },
    {
      code: "5rem",
    },
    {
      code: "6rem",
    },
    {
      code: "7rem",
    },
    {
      code: "8rem",
    },
    {
      code: "9rem",
    },
    {
      code: "10rem",
    },
    {
      code: "11rem",
    },
    {
      code: "12rem",
    },
    {
      code: "13rem",
    },
    {
      code: "14rem",
    },
    {
      code: "15rem",
    },
    {
      code: "16rem",
    },
    {
      code: "18rem",
    },
    {
      code: "20rem",
    },
    {
      code: "24rem",
    },
    {
      code: "auto",
    },
    {
      code: "8.333333%",
    },
    {
      code: "16.666667%",
    },
    {
      code: "20%",
    },
    {
      code: "25%",
    },
    {
      code: "33.333333%",
    },
    {
      code: "41.666667%",
    },
    {
      code: "40%",
    },
    {
      code: "50%",
    },
    {
      code: "58.333333%",
    },
    {
      code: "60%",
    },
    {
      code: "66.666667%",
    },
    {
      code: "75%",
    },
    {
      code: "80%",
    },
    {
      code: "83.333333%",
    },
    {
      code: "91.666667%",
    },
    {
      code: "100%",
    },
  ];

  flexOptions = [
    {
      code: "0",
    },
    {
      code: "1",
    },
  ];
  properties = {
    container: [
      {
        key: "display",
        data: { name: $localize`Display`, type: "display" },
      },
      {
        key: "width",
        data: { name: $localize`Width`, type: "dimension" },
      },
      {
        key: "height",
        data: { name: $localize`Height`, type: "dimension" },
      },
      {
        key: "margin",
        data: { name: $localize`Margin`, type: "small-dimension" },
      },
      {
        key: "padding",
        data: { name: $localize`Padding`, type: "small-dimension" },
      },
      {
        key: "background-color",
        data: { name: $localize`Background Color`, type: "color" },
      },
      {
        key: "-webkit-box-orient",
        data: { name: $localize`Orientation`, type: "orientation" },
      },
      {
        key: "-webkit-box-direction",
        data: { name: $localize`Direction`, type: "direction" },
      },
      {
        key: "-webkit-box-flex",
        data: { name: $localize`Flex`, type: "flex" },
      },
      {
        key: "border",
        data: { name: $localize`Border`, type: "border" },
      },
      {
        key: "border-top",
        data: { name: $localize`Border Top`, type: "border" },
      },
      {
        key: "border-right",
        data: { name: $localize`Border Right`, type: "border" },
      },
      {
        key: "border-bottom",
        data: { name: $localize`Border Bottom`, type: "border" },
      },
      {
        key: "border-left",
        data: { name: $localize`Border Left`, type: "border" },
      },
      {
        key: "border-radius",
        data: { name: $localize`Border radius`, type: "border-radius" },
      },
      {
        key: "float",
        data: { name: $localize`Float`, type: "float" },
      },
    ],
    list: [
      {
        key: "list-style",
        data: { name: $localize`List Style`, type: "list-style" },
      },
    ],
    text: [
      {
        key: "color",
        data: { name: $localize`Color`, type: "color" },
      },
      {
        key: "font-family",
        data: { name: $localize`Font Family`, type: "font-family" },
      },
      {
        key: "font-size",
        data: { name: $localize`Font Size`, type: "font-size" },
      },
      {
        key: "font-weight",
        data: { name: $localize`Font Weight`, type: "font-weight" },
      },
      {
        key: "text-align",
        data: { name: $localize`Text Align`, type: "text-align" },
      },
      {
        key: "line-height",
        data: { name: $localize`Line Height`, type: "line-height" },
      },
      {
        key: "text-transform",
        data: { name: $localize`Text Transform`, type: "text-transform" },
      },
      {
        key: "letter-spacing",
        data: { name: $localize`Letter Spacing`, type: "letter-spacing" },
      },
      {
        key: "vertical-align",
        data: { name: $localize`Vertical Align`, type: "vertical-align" },
      },
    ],
    image: [
      {
        key: "background-image",
        data: { name: $localize`Bg Image`, type: "background-image" },
      },
      {
        key: "background-repeat",
        data: { name: $localize`Bg Repeat`, type: "background-repeat" },
      },
      {
        key: "background-size",
        data: { name: $localize`Bg Size`, type: "background-size" },
      },
      {
        key: "background-position",
        data: { name: $localize`Bg Position`, type: "background-position" },
      },
    ],
  };

  @Output()
  brandingChange = new EventEmitter<string>();

  @Input()
  set branding(branding: string) {
    if (this._branding !== branding) {
      this._branding = branding;
      this.initDataFromCSS();
      this.brandingChange.emit(this._branding);
    }
  }

  get branding(): string {
    return this._branding;
  }

  @Output()
  styleChange = new EventEmitter<string>();

  @Input()
  set style(style: string) {
    if (this._style !== style) {
      this._style = style;
      this.initDataFromCSS();
      this.styleChange.emit(this._style);
    }
  }

  get style(): string {
    return this._style;
  }

  brandVariables = [
    {
      key: "colors",
      data: {
        name: "Colors",
      },
      children: [
        {
          key: "primary-color",
          data: { name: `Primary Color`, type: "color", value: null },
        },
        {
          key: "secondary-color",
          data: { name: `Secondary Color`, type: "color", value: null },
        },
        {
          key: "contrast-color",
          data: { name: `Tertiary Color`, type: "color", value: null },
        },
      ],
    },
    {
      key: "fonts",
      data: { name: "Fonts" },
      children: [
        {
          key: "primary-font-family",
          data: { name: `Primary Font`, type: "font-family", value: null },
        },
        {
          key: "secondary-font-family",
          data: { name: `Secondary Font`, type: "font-family", value: null },
        },
      ],
    },
  ];
  pageTypes = [
    {
      selector: ".nis-page",
      key: "catalog",
      data: { name: $localize`General` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: "h1",
          key: "h1",
          data: { name: `H1`, type: "text" },
          children: [],
        },
        {
          selector: "h2",
          key: "h2",
          data: { name: `H2`, type: "text" },
          children: [],
        },
        {
          selector: "h3",
          key: "h3",
          data: { name: `H3`, type: "text" },
          children: [],
        },
        {
          selector: "h4",
          key: "h4",
          data: { name: `H4`, type: "text" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
        {
          selector: "table",
          key: "table",
          data: { name: `Table`, type: "container" },
          children: [],
        },
        {
          selector: "thead",
          key: "table-header",
          data: { name: `Table Header`, type: "container" },
          children: [],
        },
        {
          selector: "tbody",
          key: "table-body",
          data: { name: `Table Body`, type: "container" },
          children: [],
        },
        {
          selector: "tr",
          key: "table-row",
          data: { name: `Table Row`, type: "container" },
          children: [],
        },
        {
          selector: "td",
          key: "table-cell",
          data: { name: `Table Cell`, type: "container" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-front-cover",
      key: "front-cover",
      data: { name: $localize`Front Cover` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Front Cover Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-kind-about_us",
      key: "about-us",
      data: { name: $localize`About Us` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `About Us Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-kind-table_of_contents",
      key: "toc",
      data: { name: $localize`Table Of Content` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Table Of Content Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
        // {
        //   selector: "h2",
        //   key: "title",
        //   data: { name: `Title`, type: "text" },
        //   children: [],
        // },
        // {
        //   selector: "p",
        //   key: "content",
        //   data: { name: `Content`, type: "text" },
        //   children: [],
        // },
        {
          selector: ".nis-toc-index",
          key: "content-index",
          data: { name: `List`, type: "list" },
          children: [],
        },
        {
          selector: ".nis-toc-index-item",
          key: "content-index-item",
          data: { name: `List Item`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-toc-page-title",
          key: "content-section-title",
          data: { name: `List Title`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-toc-page-number",
          key: "content-section-page-number",
          data: { name: `Page Number`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-toc-page-delimiter",
          key: "content-section-page-delimiter",
          data: { name: `Page Delimiter`, type: "container" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-product",
      key: "product-page",
      data: { name: $localize`Product Page` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Product Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-header",
          key: "header",
          data: { name: `Page Header`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-header-text",
          key: "header-text",
          data: { name: `Page Header Text`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-header-text h2",
          key: "header-title",
          data: { name: `Page Header Title`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-page-cell",
          key: "cell",
          data: { name: `Page Cell`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-footer",
          key: "footer",
          data: { name: `Product Page Footer`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-footer-text",
          key: "footer-center",
          data: { name: `Product Page Footer Center`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-footer-text p",
          key: "footer-text",
          data: { name: `Product Page Footer Text`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-page-footer-number",
          key: "footer-page-number",
          data: { name: `Product Page Number`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-footer-number-text",
          key: "footer-page-number-text",
          data: { name: `Product Page Number Text`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-page-footer-logo",
          key: "footer-logo",
          data: { name: `Product Page Footer Logo`, type: "image" },
          children: [],
        },
        {
          selector: ".nis-cell-listing",
          key: "cell-listing",
          data: { name: `Page Product Listing`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-cell-listing-image",
          key: "cell-image",
          data: { name: `Page Product Image`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-cell-caption",
          key: "caption",
          data: { name: `Page Product Caption`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-caption-title",
          key: "caption-title",
          data: { name: `Product Title`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-caption-sku",
          key: "caption-sku",
          data: { name: `Product SKU`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-caption-price",
          key: "caption-price",
          data: { name: `Product Price`, type: "text" },
          children: [],
        },
        {
          selector: ".nis-caption-msrp",
          key: "caption-msrp",
          data: { name: `Product MSRP`, type: "text" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-back-cover",
      key: "back-cover",
      data: { name: $localize`Back Cover` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Back Cover Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-kind-wholesale-contract",
      key: "wholesale-contract",
      data: { name: $localize`Wholesale Contract` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Wholesale Contract Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
      ],
    },
    {
      selector: ".nis-page-kind-wholesale-order-form",
      key: "wholesale-order-form",
      data: { name: $localize`Wholesale Order Form` },
      sections: [
        {
          selector: "",
          key: "page",
          data: { name: `Wholesale Order Form Page`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-content",
          key: "page-content",
          data: { name: `Page Content`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-page-body",
          key: "body",
          data: { name: `Page Body`, type: "container" },
          children: [],
        },
        {
          selector: "p",
          key: "paragraph",
          data: { name: `Paragraph`, type: "text" },
          children: [],
        },
        {
          selector: "table",
          key: "table",
          data: { name: `Table`, type: "container" },
          children: [],
        },
        {
          selector: "thead",
          key: "table-header",
          data: { name: `Table Header`, type: "container" },
          children: [],
        },
        {
          selector: "tbody",
          key: "table-body",
          data: { name: `Table Body`, type: "container" },
          children: [],
        },
        {
          selector: "tr",
          key: "table-row",
          data: { name: `Table Row`, type: "container" },
          children: [],
        },
        {
          selector: "td",
          key: "table-cell",
          data: { name: `Table Cell`, type: "container" },
          children: [],
        },
        {
          selector: ".nis-wholesale-form-header",
          key: "wholesale-form-header",
          data: { name: `Wholesale Form Header`, type: "container" },
          children: [],
        },
      ],
    },
  ];

  pageType = this.pageTypes[0];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    for (let pageType of this.pageTypes) {
      for (let section of pageType.sections) {
        section.children = this.initChildrenNodes(section);
      }
    }

    this.initDataFromCSS();
  }

  initDataFromCSS() {
    if (this.branding?.length > 0 && this.style?.length > 0) {
      const parsedCss = parse(this.branding + "\n" + this.style);
      const variableIndex = this.indexStyle();

      let errors = 0;
      parsedCss.stylesheet.rules.forEach((rule: any) => {
        if (
          rule.selectors?.includes(".nis-catalog") ||
          rule.selectors?.includes("body")
        ) {
          rule.declarations.forEach((declaration: any) => {
            if (declaration.property.startsWith("--")) {
              if (variableIndex[declaration.property]) {
                variableIndex[declaration.property].data.value =
                  declaration.value;
              } else {
                errors++;
              }
            }
          });
        }
      });
      console.warn("property not found count ", errors);
    }
  }

  initChildrenNodes(section) {
    var properties = [];
    switch (section.data.type) {
      case "container":
        properties = [
          ...JSON.parse(JSON.stringify(this.properties.container)),
          {
            key: "advanced",
            data: { name: "Advanced" },
            name: "Advanced",
            children: [
              ...JSON.parse(JSON.stringify(this.properties.text)),
              ...JSON.parse(JSON.stringify(this.properties.image)),
            ],
          },
        ];
        break;
      case "list":
        properties = [
          ...JSON.parse(JSON.stringify(this.properties.list)),
          ...JSON.parse(JSON.stringify(this.properties.container)),
          {
            key: "advanced",
            data: { name: "Advanced" },
            name: "Advanced",
            children: [
              ...JSON.parse(JSON.stringify(this.properties.text)),
              ...JSON.parse(JSON.stringify(this.properties.image)),
            ],
          },
        ];
        break;
      case "text":
        properties = [
          ...JSON.parse(JSON.stringify(this.properties.text)),
          {
            key: "advanced",
            data: { name: "Advanced" },
            name: "Advanced",
            children: [
              ...JSON.parse(JSON.stringify(this.properties.container)),
              ...JSON.parse(JSON.stringify(this.properties.image)),
            ],
          },
        ];
        break;
      case "image":
        properties = [
          ...JSON.parse(JSON.stringify(this.properties.image)),
          {
            key: "advanced",
            data: { name: "Advanced" },
            name: "Advanced",
            children: [
              ...JSON.parse(JSON.stringify(this.properties.container)),
            ],
          },
        ];
        break;
      default:
        break;
    }
    return properties;
  }

  slugName(name: string) {
    return name.replace(/-/g, "");
  }

  updateStyleForItem(prefix, item) {
    let style = "";
    if (item.children?.length > 0) {
      for (let subItem of item.children) {
        style += this.updateStyleForItem(prefix, subItem);
      }
    } else if (item.data.value) {
      style += `${prefix}-${item.key}: ${item.data.value};\n`;
    }
    return style;
  }
  updateStyleSelectorForItem(prefix, item) {
    let style = "";
    if (item.children?.length > 0) {
      for (let subItem of item.children) {
        style += this.updateStyleSelectorForItem(prefix, subItem);
      }
    } else if (item.data.value) {
      style += `\t${item.key}: var(${prefix}-${item.key}) !important;\n`;
    }
    return style;
  }
  async updateStyle() {
    const response = await firstValueFrom(
      await this.http.get(
        `https://www.googleapis.com/webfonts/v1/webfonts?key=${environment.google_font_api_key}`
      )
    );
    const fonts = response["items"];
    let branding = "";
    for (let child of this.brandVariables[1].children) {
      if (child.data.value) {
        const font = _.find(fonts, {
          family: child.data.value.replace(/'/g, "").replace(/"/g, ""),
        });
        let variants = "";
        if (font) {
          variants = font.variants.join(",");
        }
        branding += `@import url('https://fonts.googleapis.com/css?family=${encodeURIComponent(
          child.data.value.replace(/'/g, "").replace(/"/g, "") +
            (variants.length > 0 ? ":" + variants : "")
        )}');`;
      }
    }
    branding += `\n`;
    branding += "body {\n";
    for (let brandVariable of this.brandVariables) {
      branding += `\t/* ${brandVariable.data.name} */\n`;
      for (let item of brandVariable.children) {
        branding += this.updateStyleForItem(`\t-`, item);
      }
      branding += `\n`;
    }
    branding += "}";
    this.branding = branding;
    let style = this.rootCSS + "\n\n";
    style += ".nis-catalog {\n";
    let styleSelectors = "";
    for (let pageType of this.pageTypes) {
      for (let section of pageType.sections) {
        style += `\t/* ${pageType.data.name} ${section.data.name} */\n`;
        styleSelectors += `.nis-catalog ${pageType.selector} ${section.selector} {\n`;
        for (let item of section.children) {
          style += this.updateStyleForItem(
            `\t--${pageType.key}-${section.key}`,
            item
          );
          styleSelectors += this.updateStyleSelectorForItem(
            `--${pageType.key}-${section.key}`,
            item
          );
        }
        styleSelectors += `}\n`;
        style += "\n";
      }
    }
    style += "}";
    this.style = style + "\n\n" + styleSelectors;
  }

  indexStyleForItem(variableIndex, prefix, item) {
    if (item.children?.length > 0) {
      for (let subItem of item.children) {
        this.indexStyleForItem(variableIndex, prefix, subItem);
      }
    } else {
      variableIndex[`${prefix}-${item.key}`] = item;
    }
  }
  indexStyle() {
    const variableIndex = {};
    for (let brandVariable of this.brandVariables) {
      for (let item of brandVariable.children) {
        this.indexStyleForItem(variableIndex, `-`, item);
      }
    }
    for (let pageType of this.pageTypes) {
      for (let section of pageType.sections) {
        for (let item of section.children) {
          this.indexStyleForItem(
            variableIndex,
            `--${pageType.key}-${section.key}`,
            item
          );
        }
      }
    }
    return variableIndex;
  }

  show(pageTypeSelected: string, sectionSelected: string) {
    for (let pageType of this.pageTypes) {
      if (
        pageTypeSelected.split(" ").includes(pageType.selector.replace(".", ""))
      ) {
        this.pageType = pageType;
      }
    }

    this.currentSections = [];
    for (let section of this.pageType.sections) {
      console.log(
        section.selector,
        sectionSelected.split(","),
        sectionSelected.split(",").includes(section.selector.replace(".", ""))
      );
      if (
        section.selector.length === 0 ||
        sectionSelected.split(",").includes(section.selector.replace(".", ""))
      ) {
        this.currentSections.push(section);
      }
    }
  }
}
